import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, CheckCircle2, AlertTriangle, XCircle, ShieldAlert, Link2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { agentProofChainStatus } from '@/data/mockData';
import GradeTooltip from '@/components/GradeTooltip';

const getAgentChainGrade = (id) => {
  const s = agentProofChainStatus.find(a => a.agentId === id);
  return s ? s.grade : 'UNAUDITED';
};

const agents = [
  { id: 'classifier-agent-01', purpose: 'Contract classification', permissions: 'Read: Contract corpus, Classification schema', lastAction: 'Apr 15, 14:32', controls: '9/9', status: 'active',
    overview: 'Primary classification agent for incoming contract submissions. Processes ~800 submissions daily with 99.2% accuracy.',
    tools: ['Contract Corpus Reader', 'Schema Lookup', 'Classification Writer'],
    delegated: ['Read: Contract Corpus 2024', 'Read: Classification Schema v9', 'Write: Classification Results DB'],
  },
  { id: 'legal-draft-agent-02', purpose: 'Legal document drafting', permissions: 'Read: Legal corpus · Write: Draft docs', lastAction: 'Apr 15, 14:31', controls: '6/9', status: 'gap',
    overview: 'Drafting recommendations agent for legal document request portal. 3 compliance gaps under remediation.',
    principal: 'ldrs-workflow@meridian.global',
    created: 'Jan 14, 2026',
    lastActive: 'Apr 15, 2026 · 14:31 UTC',
    deployment: 'Legal Document Drafting Pipeline',
    riskFlags: [
      { type: 'warning', text: 'Training data lineage unverified — External Legal Corpus dependency not confirmed as trusted source' },
      { type: 'critical', text: 'EU AI Act Art. 13 Transparency obligation — documentation incomplete. Human reviewer notification flow not fully documented.' },
    ],
    compliance: { euAiAct: '6/9 · 3 gaps', iso42001: '5/8 · 3 gaps', nist: '7/10 · 3 gaps' },
    recentActions: [
      { time: 'Apr 15, 14:31', action: 'Draft generated', target: 'Legal request LR-4421', status: 'Approved by human reviewer' },
      { time: 'Apr 15, 13:44', action: 'Draft generated', target: 'Legal request LR-4420', status: 'Approved by human reviewer' },
      { time: 'Apr 15, 12:19', action: 'Write access requested', target: 'Legal doc repository', status: 'Pending approval' },
      { time: 'Apr 15, 11:05', action: 'Draft generated', target: 'Legal request LR-4418', status: 'Approved by human reviewer' },
    ],
    tools: ['Legal Corpus Search', 'Draft Generator', 'Citation Checker'],
    delegated: ['Read: Internal Legal Corpus', 'Read: Staff Identity Service', 'Write: Draft Documents', 'Read: Request Metadata'],
  },
  { id: 'academy-recommender-01', purpose: 'Training recommendations', permissions: 'Read: Course catalog, User profiles', lastAction: 'Apr 15, 14:15', controls: '8/8', status: 'active',
    overview: 'Personalized learning path agent for Meridian Academy platform.',
    tools: ['Course Catalog API', 'User Profile Reader', 'Recommendation Engine'],
    delegated: ['Read: Course Catalog', 'Read: User Interaction Logs', 'Read: Course Metadata'],
  },
  { id: 'doc-processor-03', purpose: 'Document processing', permissions: 'Read: Submissions', lastAction: 'Apr 15, 13:58', controls: '0/8', status: 'unaudited',
    overview: 'Document summarization and extraction agent for claims case files. UNAUDITED — no compliance controls mapped.',
    tools: ['Document Parser', 'Summary Generator', 'Entity Extractor'],
    delegated: ['Read: Claims Case Documents'],
  },
  { id: 'metadata-tagger-01', purpose: 'Content metadata tagging', permissions: 'Read: Media assets', lastAction: 'Apr 14, 09:22', controls: '5/5', status: 'active',
    overview: 'Automated metadata extraction agent for webcast and media archives.',
    tools: ['Media Asset Reader', 'Metadata Schema', 'Tag Writer'],
    delegated: ['Read: Media Archive', 'Read: Metadata Schema', 'Write: Tag Database'],
  },
  { id: 'audit-assistant-01', purpose: 'Compliance report drafting', permissions: 'Read: Decision ledger, Gap register', lastAction: 'Apr 13, 16:44', controls: '7/7', status: 'active',
    overview: 'Internal audit support agent. Generates compliance reports and evidence summaries for audit committees.',
    tools: ['Ledger Reader', 'Gap Register API', 'Report Generator'],
    delegated: ['Read: Decision Ledger', 'Read: Gap Register', 'Read: Compliance Framework DB'],
  },
];

const approvalQueue = [
  { agent: 'legal-draft-agent-02', request: 'Requesting write access to finalized legal document repository', risk: 'HIGH', workflow: 'Legal Document Drafting', time: 'Apr 15, 14:28' },
  { agent: 'doc-processor-03', request: 'Requesting access to external submission portal API', risk: 'MEDIUM', workflow: 'Claims Document Processing', time: 'Apr 15, 13:55' },
  { agent: 'classifier-agent-01', request: 'Requesting batch classification of 847 new applications', risk: 'LOW', workflow: 'Contract Classification', time: 'Apr 15, 14:30' },
];

const statusConfig = {
  active: { color: '#10B981', label: 'Active', icon: CheckCircle2 },
  gap: { color: '#F59E0B', label: 'Gap', icon: AlertTriangle },
  unaudited: { color: '#EF4444', label: 'Unaudited', icon: XCircle },
};

function AgentDetail({ agent, onClose }) {
  if (!agent) return <div className="flex items-center justify-center h-full text-[#475569] text-sm">Select an agent to view details</div>;
  const sc = statusConfig[agent.status];
  return (
    <ScrollArea className="h-full">
      <div className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Bot size={16} className="text-[#6366F1]" />
          <span className="font-mono text-sm font-bold text-[#F8FAFC]">{agent.id}</span>
        </div>
        <div className="text-xs text-[#64748B] mb-4">{agent.purpose}</div>
        <Tabs defaultValue="overview">
          <TabsList className="bg-transparent border-b border-[#1E2A45] rounded-none h-auto py-0 justify-start gap-0 mb-4">
            {['Overview', 'Tools', 'Permissions', 'History'].map((tab) => (
              <TabsTrigger key={tab} value={tab.toLowerCase()} className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#6366F1] data-[state=active]:text-[#6366F1] text-[#64748B] text-[11px] px-3 py-2 bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none">{tab}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="mt-0 space-y-3">
            <div className="text-xs text-[#94A3B8] leading-relaxed">{agent.overview}</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-[#475569]">Status:</span> <span className="font-medium" style={{ color: sc.color }}>{sc.label}</span></div>
              <div><span className="text-[#475569]">Controls:</span> <span className="font-mono text-[#94A3B8]">{agent.controls}</span></div>
              <div className="flex items-center gap-1.5">
                <Link2 size={10} className="text-[#475569]" />
                <span className="text-[#475569]">Proof Chain:</span>
                {(() => {
                  const g = getAgentChainGrade(agent.id);
                  return <GradeTooltip grade={g === 'UNAUDITED' ? 'UNAUDITED' : g}>
                    <span className={`font-mono text-[10px] cursor-help ${g === 'AUDIT-GRADE' ? 'text-[#10B981]' : g === 'OPERATIONS-GRADE' ? 'text-[#F59E0B]' : 'text-[#EF4444]'}`}>
                      {g === 'UNAUDITED' ? '○ UNAUDITED' : `◆ ${g}`}
                    </span>
                  </GradeTooltip>;
                })()}
              </div>
              {agent.principal && <div><span className="text-[#475569]">Principal:</span> <span className="font-mono text-[#94A3B8] text-[10px]">{agent.principal}</span></div>}
              {agent.created && <div><span className="text-[#475569]">Created:</span> <span className="text-[#94A3B8]">{agent.created}</span></div>}
              {agent.deployment && <div className="col-span-2"><span className="text-[#475569]">Pipeline:</span> <span className="text-[#94A3B8]">{agent.deployment}</span></div>}
              {agent.lastActive && <div className="col-span-2"><span className="text-[#475569]">Last active:</span> <span className="font-mono text-[#94A3B8] text-[10px]">{agent.lastActive}</span></div>}
            </div>
            {/* Unaudited agent alert */}
            {getAgentChainGrade(agent.id) === 'UNAUDITED' && (
              <div className="mt-3 p-3 rounded-lg bg-[#EF4444]/5 border border-[#EF4444]/20" data-testid="unaudited-agent-alert">
                <div className="text-[10px] font-bold text-[#EF4444] flex items-center gap-1"><AlertTriangle size={11} /> NO GOVERNANCE PROOF CHAIN</div>
                <div className="text-[10px] text-[#94A3B8] mt-1">This agent is making decisions with no Classification Evidence Record, no Control State capture, and no Oversight Evidence Records.</div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">0/8 compliance controls mapped. Decisions cannot be included in any evidence package. <span className="text-[#EF4444] font-medium">These decisions are invisible to auditors.</span></div>
                <div className="flex items-center gap-3 mt-2">
                  <button onClick={() => toast.info('Starting classification review')} className="text-[10px] text-[#EF4444] font-medium border border-[#EF4444]/30 hover:bg-[#EF4444]/10 px-3 py-1.5 rounded-lg transition-colors">Start Classification →</button>
                  <Link to="/dashboard/proof-chain" className="text-[10px] text-[#6366F1] hover:text-[#818cf8]">View in Proof Chain →</Link>
                </div>
              </div>
            )}
            {agent.riskFlags?.length > 0 && (
              <div>
                <div className="text-[10px] text-[#475569] uppercase mb-2">Risk Flags ({agent.riskFlags.length})</div>
                {agent.riskFlags.map((f, i) => (
                  <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg mb-1.5 text-xs ${f.type === 'critical' ? 'bg-[#EF4444]/5 border border-[#EF4444]/20 text-[#EF4444]' : 'bg-[#F59E0B]/5 border border-[#F59E0B]/20 text-[#F59E0B]'}`}>
                    <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />{f.text}
                  </div>
                ))}
              </div>
            )}
            {agent.compliance && (
              <div>
                <div className="text-[10px] text-[#475569] uppercase mb-2">Compliance Controls</div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between"><span className="text-[#94A3B8]">EU AI Act</span><span className="font-mono text-[#F59E0B]">{agent.compliance.euAiAct}</span></div>
                  <div className="flex justify-between"><span className="text-[#94A3B8]">ISO 42001</span><span className="font-mono text-[#F59E0B]">{agent.compliance.iso42001}</span></div>
                  <div className="flex justify-between"><span className="text-[#94A3B8]">NIST AI RMF</span><span className="font-mono text-[#F59E0B]">{agent.compliance.nist}</span></div>
                </div>
              </div>
            )}
            {agent.recentActions && (
              <div>
                <div className="text-[10px] text-[#475569] uppercase mb-2">Recent Actions</div>
                <div className="space-y-1.5">
                  {agent.recentActions.map((a, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded bg-[#080D1A] border border-[#1E2A45] text-[10px]">
                      <span className="font-mono text-[#475569] flex-shrink-0 w-20">{a.time}</span>
                      <span className="text-[#94A3B8] flex-1">{a.action} · {a.target}</span>
                      <span className={`flex-shrink-0 ${a.status.includes('Pending') ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>{a.status.includes('Pending') ? '⏳' : '✓'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="tools" className="mt-0 space-y-2">
            {agent.tools.map((t) => (
              <div key={t} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] text-xs text-[#94A3B8]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#6366F1]" />{t}
              </div>
            ))}
          </TabsContent>
          <TabsContent value="permissions" className="mt-0 space-y-2">
            {agent.delegated.map((d) => (
              <div key={d} className="flex items-center gap-2 p-2.5 rounded-lg bg-[#080D1A] border border-[#1E2A45] text-xs text-[#94A3B8]">
                <CheckCircle2 size={12} className="text-[#10B981]" />{d}
              </div>
            ))}
          </TabsContent>
          <TabsContent value="history" className="mt-0">
            <div className="text-xs text-[#64748B]">Recent actions logged in Decision Ledger.</div>
          </TabsContent>
        </Tabs>
      </div>
    </ScrollArea>
  );
}

export default function AgentControls() {
  const [selectedAgent, setSelectedAgent] = useState(agents.find(a => a.id === 'legal-draft-agent-02'));
  const activeCount = agents.filter((a) => a.status === 'active').length;
  const blocked = 7;

  return (
    <div data-testid="agent-controls" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Active Agents</div><div className="text-2xl font-bold text-[#F8FAFC]">{agents.length}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Requiring Approval</div><div className="text-2xl font-bold text-[#F59E0B]">{approvalQueue.length}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Blocked Actions Today</div><div className="text-2xl font-bold text-[#EF4444]">{blocked}</div></div>
        <div className="glass-card p-4"><div className="text-[10px] text-[#475569] uppercase tracking-wider mb-1">Delegated Permissions</div><div className="text-2xl font-bold text-[#F8FAFC]">24</div></div>
      </div>

      {/* Agent Registry + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 340 }}>
        <div className="lg:col-span-3 glass-card overflow-hidden">
          <div className="px-3 pt-3 pb-0">
            <div className="text-[9px] text-[#475569] mb-2">Supports: ISO 27001 A.9 · NIST 800-53 AC-6 · SOC 2 CC6.3</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#1E2A45]">
                {['Agent ID', 'Purpose', 'Permissions', 'Last Action', 'Controls', 'Status'].map((h) => (
                  <th key={h} className="text-left text-[10px] font-medium text-[#475569] uppercase tracking-wider py-3 px-3">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {agents.map((agent) => {
                  const sc = statusConfig[agent.status];
                  const Icon = sc.icon;
                  return (
                    <tr key={agent.id} data-testid={`agent-row-${agent.id}`} onClick={() => setSelectedAgent(agent)} className={`border-b border-[#1E2A45]/50 table-row-interactive ${selectedAgent?.id === agent.id ? 'bg-[#6366F1]/5' : ''}`}>
                      <td className="py-2.5 px-3 font-mono text-[#6366F1] text-xs">{agent.id}</td>
                      <td className="py-2.5 px-3 text-[#F8FAFC] text-xs">{agent.purpose}</td>
                      <td className="py-2.5 px-3 text-[#64748B] text-[11px] max-w-[180px] truncate">{agent.permissions}</td>
                      <td className="py-2.5 px-3 font-mono text-[#94A3B8] text-[11px]">{agent.lastAction}</td>
                      <td className="py-2.5 px-3 font-mono text-[#94A3B8] text-xs">{agent.controls}</td>
                      <td className="py-2.5 px-3"><span className="flex items-center gap-1 text-[10px] font-medium" style={{ color: sc.color }}><Icon size={12} />{sc.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="lg:col-span-2 glass-card overflow-hidden" data-testid="agent-detail-panel">
          <AgentDetail agent={selectedAgent} />
        </div>
      </div>

      {/* Human Approval Queue */}
      <div className="glass-card p-6" data-testid="approval-queue">
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert size={16} className="text-[#F59E0B]" />
          <h3 className="text-sm font-semibold text-[#F8FAFC]">Actions Requiring Human Approval</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {approvalQueue.map((item) => {
            const riskCls = item.risk === 'HIGH' ? 'risk-high' : item.risk === 'MEDIUM' ? 'risk-medium' : 'risk-low';
            return (
              <div key={item.agent + item.request} className="p-4 rounded-lg bg-[#080D1A] border border-[#1E2A45]" data-testid={`approval-${item.agent}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-xs text-[#6366F1]">{item.agent}</span>
                  <span className={`${riskCls} text-[10px] font-mono px-2 py-0.5 rounded-full`}>{item.risk}</span>
                </div>
                <div className="text-xs text-[#F8FAFC] mb-1">{item.request}</div>
                <div className="text-[10px] text-[#475569] mb-3">Workflow: {item.workflow} · {item.time}</div>
                <div className="flex gap-2">
                  <button onClick={() => toast.success(`Approved: ${item.request}`)} className="flex-1 bg-[#10B981] hover:bg-[#059669] text-white text-[10px] font-medium py-1.5 rounded-lg transition-all">Approve</button>
                  <button onClick={() => toast.error(`Rejected: ${item.request}`)} className="flex-1 bg-transparent border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 text-[10px] font-medium py-1.5 rounded-lg transition-all">Reject</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
